import React from 'react';
import './AppPricingBadge.css';

/**
 * Tier definitions — mirror the backend's pricing-rates.js source of
 * truth. Values are in milli-cents-per-minute. When backend rates
 * change we should source them from /credits/balance's `activeRates`
 * but this fallback table is what we render before the API replies (so
 * the badge isn't blank on cold load).
 *
 * Tier names map to the catalog entry's `pricing.tier` field.
 */
export const TIER_FALLBACKS = {
  light:    { label: 'Light',    milliCentsPerMin:  83,  hourly: '$0.05/hr' },   //  $0.0498/hr  = 5 mc/min
  standard: { label: 'Standard', milliCentsPerMin: 200,  hourly: '$0.12/hr' },   //  $0.12/hr   = 200 mc/min
  heavy:    { label: 'Heavy',    milliCentsPerMin: 417,  hourly: '$0.25/hr' },   //  $0.2502/hr = 417 mc/min
};

const TIER_TO_VARIANT = {
  light: 'green',
  standard: 'blue',
  heavy: 'orange',
};

/**
 * Resolve a tier name → display info. Defaults to "standard" when the
 * catalog entry hasn't been migrated yet (Agent C is back-filling tier
 * data — until then this prevents the badge from disappearing).
 */
export function resolveTier(tier) {
  const key = String(tier || 'standard').toLowerCase();
  return {
    key,
    ...(TIER_FALLBACKS[key] || TIER_FALLBACKS.standard),
    variant: TIER_TO_VARIANT[key] || 'blue',
  };
}

/**
 * Compute the approximate monthly cost if the app runs 24/7. Returns a
 * formatted $X/mo string. Used in the hover tooltip.
 */
function approxMonthly(milliCentsPerMin) {
  const hours = 24 * 30; // ≈ 720 hours
  const usd = (milliCentsPerMin * 60 * hours) / 100000;
  return `$${usd.toFixed(2)}/mo`;
}

/**
 * AppPricingBadge — small pill rendered on each marketplace card.
 *
 * Variants (driven by tier): green / blue / orange.
 * Hover: shows "≈ $X per month if always on" via native title attribute.
 */
const AppPricingBadge = ({ tier, className = '' }) => {
  const info = resolveTier(tier);
  const tooltip = `${info.label} • ${info.hourly} · ≈ ${approxMonthly(info.milliCentsPerMin)} if always on`;
  return (
    <span
      className={`app-pricing-badge app-pricing-badge--${info.variant} ${className}`.trim()}
      title={tooltip}
    >
      <span className="app-pricing-badge-tier">{info.label}</span>
      <span className="app-pricing-badge-sep">·</span>
      <span className="app-pricing-badge-rate">{info.hourly}</span>
    </span>
  );
};

export default AppPricingBadge;
